import { InvoiceKindChip } from '../../components/InvoiceKind';
import { useEffect, useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { api } from '../../lib/api';
import { useIsMobile } from '../../lib/useIsMobile';
import { formatInvoiceNumber, formatMoneyExact, Modal } from '../../components/primitives';
import type { CutApprovedPreview } from './types';

/** "Cut approved →" automation modal (Phase 3.1, mockup #5) — replaces "Mark
    delivered" while the project sits in Post. Tasks and invoicing default on; phase advance defaults off; the invoice row shows the real computed ex-GST amount from
    the preview endpoint before Henry commits to anything. */
function CutApprovedModal({
  projectId,
  onClose,
  onApproved,
}: {
  projectId: string;
  onClose: () => void;
  onApproved: () => void;
}) {
  const navigate = useNavigate();
  const [preview, setPreview] = useState<CutApprovedPreview | null>(null);
  const [createTasks, setCreateTasks] = useState(true);
  const [advancePhase, setAdvancePhase] = useState(false);
  const [raiseInvoice, setRaiseInvoice] = useState(true);
  const [busy, setBusy] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [idempotencyKey] = useState(() => crypto.randomUUID());
  // The sheet header is narrow — the desktop title wraps to two lines there,
  // so mobile takes the mockup's short form (§6.3 H). The "These run
  // immediately on Approve" hint already rides above the sheet's buttons.
  const isMobile = useIsMobile();

  useEffect(() => {
    api<CutApprovedPreview>(`/api/admin/projects/${projectId}/cut-approved-preview`)
      .then(setPreview)
      .catch(() => setError('Could not load the automation preview.'));
  }, [projectId]);

  const approve = async () => {
    setBusy(true);
    setError(null);
    try {
      const result = await api<{ invoice: { id: string; reused?: boolean } | null }>(`/api/admin/projects/${projectId}/cut-approved`, {
        method: 'POST',
        json: {
          create_tasks: createTasks,
          advance_phase: advancePhase,
          raise_invoice: raiseInvoice,
          idempotency_key: idempotencyKey,
        },
      });
      onApproved();
      if (result.invoice?.reused) navigate(`/finance/invoices/${result.invoice.id}`);
    } catch (err) {
      setError(err instanceof Error ? err.message : 'Could not approve the cut.');
      setBusy(false);
    }
  };

  return (
    <Modal
      title={isMobile ? 'Approve cut' : 'Approve cut — automation'}
      context="PROJECT"
      mobile="half"
      onClose={onClose}
      hint="These run immediately on Approve"
      foot={
        <>
          <button className="btn" onClick={onClose}>
            Cancel
          </button>
          <button className="btn primary" onClick={approve} disabled={busy || !preview}>
            Approve →
          </button>
        </>
      }
    >
      {!preview && !error && <p className="empty-note">Loading…</p>}
      {preview && (
        <>
          <ConfirmRow
            checked={createTasks}
            onToggle={() => setCreateTasks((v) => !v)}
            title={`Create ${preview.tasks.length} follow-up task${preview.tasks.length === 1 ? '' : 's'}`}
            sub={preview.tasks.map((t) => `"${t}"`).join(' and ')}
          />
          {preview.can_advance_phase && (
            <ConfirmRow
              checked={advancePhase}
              onToggle={() => setAdvancePhase((v) => !v)}
              title="Advance phase to Delivered"
              sub="Keep in Post until final payment. Final files stay locked until paid."
            />
          )}
          <ConfirmRow
            checked={raiseInvoice}
            onToggle={() => setRaiseInvoice((v) => !v)}
            title={preview.invoice.existing_draft ? `Open existing draft ${formatInvoiceNumber(preview.invoice.existing_draft.invoice_number)}` : 'Raise final invoice'}
            sub={preview.invoice.existing_draft ? 'Reuse this draft — review and send manually.' : <><InvoiceKindChip kind={preview.invoice.kind} /><br />Issue date: set when you send<br />Due {preview.invoice.due_in_days} days after issue ({preview.invoice.terms_source} terms)<br />Draft only — review and send manually.</>}
            amount={preview.invoice.existing_draft ? undefined : `${formatMoneyExact(preview.invoice.ex_cents)} ex GST`}
          />
        </>
      )}
      {error && <p className="error-text">{error}</p>}
    </Modal>
  );
}

/** One checkable row in the cut-approved modal — checkbox visual matches
    .bulk-check's teal-fill tick; the whole row toggles on click. */
function ConfirmRow({
  checked,
  onToggle,
  title,
  sub,
  amount,
}: {
  checked: boolean;
  onToggle: () => void;
  title: string;
  sub: React.ReactNode;
  amount?: string;
}) {
  return (
    <div className="confirm-row" onClick={onToggle}>
      <span className={`confirm-check${checked ? ' on' : ''}`} />
      <div className="confirm-body">
        <div className={`confirm-title${checked ? '' : ' off'}`}>{title}</div>
        <div className="confirm-sub">{sub}</div>
      </div>
      {amount && <span className="confirm-amt">{amount}</span>}
    </div>
  );
}

export { CutApprovedModal };
